/**
 * Clinic detail card with read-only info and editable email (治療院情報).
 * No API calls; receives form control and email form submit from page.
 */

import AppButton from '@/components/atoms/AppButton';
import AppTextField from '@/components/atoms/AppTextField';
import { CollapsibleCard, InfoRow } from '@/components/molecules';
import { formatPeriodDisplay } from '@/utils/dateConverter';
import type { EmailUpdateFormData } from '@/schemas/clinicInformationSchema';
import { useSidebarOpen } from '@/shared/providers/SidebarContext';
import React from 'react';
import { Control, Controller, FieldErrors, UseFormHandleSubmit } from 'react-hook-form';
import type { ClinicInformation } from './types';

export interface ClinicInformationDetailSectionProps {
  clinicInfo: ClinicInformation;
  serviceType: string | null | undefined;
  control: Control<EmailUpdateFormData>;
  errors: FieldErrors<EmailUpdateFormData>;
  onSubmit: ReturnType<UseFormHandleSubmit<EmailUpdateFormData>>;
  isPending: boolean;
  primaryButtonColor: string;
}

const ClinicInformationDetailSection: React.FC<ClinicInformationDetailSectionProps> = ({
  clinicInfo,
  control,
  errors,
  onSubmit,
  isPending,
  primaryButtonColor,
}) => {
  const sidebarOpen = useSidebarOpen();
  return (
    <>
      <CollapsibleCard title="治療院情報">
        <div className="space-y-1">
          <InfoRow label="治療院ID" value={clinicInfo.clinic_id_display} />
          <InfoRow label="治療院名" value={clinicInfo.name} />
          {/* <InfoRow
          label="サービス種別"
          value={
            <AppTag
              className="inline-block px-2 py-0.5 rounded text-sm text-white border-0"
              style={{ backgroundColor: sectionHeaderColor }}
            >
              {getServiceTypeDisplay(serviceType)}
            </AppTag>
          }
        /> */}
          <InfoRow
            label="適用期間"
            value={
              formatPeriodDisplay(clinicInfo.available_from, clinicInfo.available_to) || undefined
            }
          />
          <InfoRow
            label="郵便番号"
            value={clinicInfo.postal_code ? `〒${clinicInfo.postal_code}` : null}
          />
          <InfoRow label="住所" value={clinicInfo.full_address} />
          <InfoRow label="電話番号" value={clinicInfo.telephone} />
          <InfoRow label="FAX" value={clinicInfo.fax} />
          <InfoRow label="携帯" value={clinicInfo.mobile} />

          <div className="flex items-center py-1">
            <div className="w-1/6 font-medium text-gray-700">メールアドレス</div>
            <div className="flex-1">
              <form id="clinic-email-form" onSubmit={onSubmit} className="inline">
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AppTextField
                      {...field}
                      type="text"
                      className="w-1/3 font-medium"
                      status={fieldState.error ? 'error' : undefined}
                      aria-label="メールアドレス"
                    />
                  )}
                />
              </form>
              {errors.email && (
                <div className="mt-1 text-sm text-red-500">{errors.email.message}</div>
              )}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <div
        className={`fixed bottom-0 left-0 right-0 z-50 p-3 bg-gray-700 shadow-lg z-41 transition-[left] duration-300 ease-in-out ${sidebarOpen ? 'left-60' : 'left-0'}`}
      >
        <div className="flex justify-end">
          <AppButton
            type="primary"
            htmlType="submit"
            form="clinic-email-form"
            loading={isPending}
            disabled={isPending}
            style={{ backgroundColor: primaryButtonColor }}
            className="px-6 py-2 text-white rounded hover:opacity-90 disabled:opacity-50"
          >
            {isPending ? '更新中...' : 'メールアドレス更新'}
          </AppButton>
        </div>
      </div>
    </>
  );
};

export default ClinicInformationDetailSection;
